import { useState } from "react";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import SvgIcon from "@mui/material/SvgIcon";

/* Custom Article Icon from Figma */
const ArticleIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 18 18">
        <path
            d="M15.75 6V15.7448C15.7507 15.8432 15.732 15.9409 15.6949 16.0322C15.6579 16.1234 15.6032 16.2065 15.534 16.2766C15.4649 16.3468 15.3826 16.4026 15.2919 16.4409C15.2011 16.4792 15.1037 16.4993 15.0052 16.5H2.99475C2.79736 16.5 2.60804 16.4216 2.4684 16.2821C2.32875 16.1426 2.2502 15.9534 2.25 15.756V2.244C2.25 1.84125 2.58675 1.5 3.0015 1.5H11.2477L15.75 6ZM14.25 6.75H10.5V3H3.75V15H14.25V6.75ZM6 5.25H8.25V6.75H6V5.25ZM6 8.25H12V9.75H6V8.25ZM6 11.25H12V12.75H6V11.25Z"
            fill="#FB923C" // Orange color from SVG
        />
    </SvgIcon>
);

/**
 * Sources Popover — Figma Node 206-8441.
 * Replaces the previous Dialog with a compact Popover.
 */
export default function SourcesDialog({ sources = [], renderTrigger, disabled }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    if (!sources.length) return null;

    return (
        <>
            {/* Render custom trigger from parent */}
            {renderTrigger && renderTrigger(handleClick)}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left", // Align left edge of popover with left edge of button?
                    // Figma design screenshot 1085 shows popover above buttons, aligned... 
                    // Actually, let's try top-left or top-center.
                    // Given it's a list, probably "bottom-left" of the button opens the popover?
                    // Or "top-left" if button is at bottom?
                    // In AIAnswerCard, buttons are at bottom. Popover should likely open UPWARDS (above button).
                    // So anchorOrigin vertical: 'top', transformOrigin vertical: 'bottom'.
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "6px",
                            boxShadow:
                                "0px 0px 2px 0px rgba(0,0,0,0.1), 0px 2px 4px 0px rgba(0,0,0,0.1)",
                            backgroundColor: "#fbfaf9", // neutral/faint
                            p: "4px", // gap/padding for list
                            mt: -1, // slight offset?
                        },
                    },
                }}
            >
                <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                    {sources.map((source, index) => {
                        const href = source.url || null;
                        const listItemSx = {
                            borderRadius: "6px",
                            p: "8px",
                            gap: "8px",
                            minHeight: "auto",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                        };
                        return (
                            <ListItemButton
                                key={source.id ?? source.kb_id ?? index}
                                component={href ? "a" : "div"}
                                href={href || undefined}
                                onClick={handleClose}
                                sx={{
                                    ...listItemSx,
                                    ...(href && { textDecoration: "none", color: "inherit" }),
                                }}
                            >
                                <ArticleIcon sx={{ fontSize: 18 }} />
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: 14,
                                        fontWeight: 400,
                                        lineHeight: "20px",
                                        color: "#3d3a37",
                                    }}
                                >
                                    {source.title ?? source.name}
                                </Typography>
                            </ListItemButton>
                        );
                    })}
                </List>
            </Popover>
        </>
    );
}
